/** Formats a number as compact string: 1,234 → 1k, 1,234,567 → 1.2M */
export const fmt = (n: number, compact = true): string => {
   if (!compact) return n.toLocaleString(undefined, { maximumFractionDigits: 0 });
   const abs  = Math.abs(n);
   const sign = n < 0 ? '-' : '';
   if (abs >= 1_000_000) return `${sign}${Math.floor(abs / 1e5) / 10}M`;
   if (abs >= 1_000)     return `${sign}${Math.floor(abs / 100) / 10}k`;
   return n.toLocaleString(undefined, { maximumFractionDigits: 1 });
};

export const fmtSigned = (n: number): string =>
   n > 0 ? `+${fmt(n)}` : fmt(n);


/**
 * Smartly formats a mixed string/number value.
 * Handles: numbers, "+1234", "1.234.567", "60.5%", "-500"
 * Leaves non-numeric strings untouched.
 */
export const fmtValue = (value: string | number, compact = true): string => {
   if (typeof value === 'number') return fmt(value, compact);
   
   if (!compact) {
      // strip any +/- prefix, parse and re-format as full number
      const s     = value.trim();
      const match = s.match(/^([+\-]?)([0-9][0-9.,]*)(.*)$/);
      if (!match) return s;
      const [, sign, raw, suffix] = match;
      const n = parseFloat(raw.replaceAll('.', '').replaceAll(',', ''));
      if (isNaN(n)) return s;
      return `${sign === '-' ? '-' : ''}${n.toLocaleString(undefined, { maximumFractionDigits: 0 })}${suffix}`;
   }
   
   const s     = value.trim();
   const match = s.match(/^([+\-]?)([0-9][0-9.,]*)(.*)$/);
   if (!match) return s;
   
   const [, sign, raw, suffix] = match;
   
   let normalised   = raw;
   const dotCount   = (raw.match(/\./g) ?? []).length;
   const commaCount = (raw.match(/,/g)  ?? []).length;
   
   if (dotCount > 1) {
      normalised = raw.replaceAll('.', '');
   } else if (commaCount > 1) {
      normalised = raw.replaceAll(',', '');
   } else if (dotCount === 1 && commaCount === 1) {
      normalised = raw.replace(',', '');
   } else if (dotCount === 1) {
      const [, decimals] = raw.split('.');
      if (decimals.length === 3) normalised = raw.replace('.', '');
   }
   
   const n = parseFloat(normalised);
   if (isNaN(n)) return s;
   
   const formatted = sign === '-' ? fmt(-n) : fmt(n);
   return `${formatted}${suffix}`;
};